/**
 * Phase 31.3 — Client-side "compatibility score" estimator for matched orders.
 *
 * UI/UX only · no backend touch · approssimazione MVP.
 *
 * Usa haversine per stimare quanto pickup/delivery dell'ordine deviano dal tragitto pendolare
 * del driver, rispetto al `max_detour_km` configurato.
 */

const KM_PER_DEG_LAT = 110.574;

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Computes match score (0-100) + verbose label + estimated detour km.
 * Returns null if mandatory coords are missing — caller should fallback to plain label.
 */
export function computeMatchScore({
  pickupLat, pickupLng,
  deliveryLat, deliveryLng,
  routeStartLat, routeStartLng,
  routeEndLat, routeEndLng,
  maxDetourKm = 5,
}) {
  const valid = [pickupLat, pickupLng, deliveryLat, deliveryLng, routeStartLat, routeStartLng, routeEndLat, routeEndLng]
    .every((v) => Number.isFinite(v));
  if (!valid) return null;
  const cap = Math.max(0.5, Math.min(maxDetourKm || 5, 50));

  const pickupDev = haversineKm(pickupLat, pickupLng, routeStartLat, routeStartLng);
  const deliveryDev = haversineKm(deliveryLat, deliveryLng, routeEndLat, routeEndLng);
  // Worst-case deviation (the one nearer the cap dominates UX perception)
  const worst = Math.max(pickupDev, deliveryDev);
  const ratio = worst / cap; // 0..>1

  // Banded score per spec: tighter than backend filter; soft cap at 40 when on the edge.
  let score;
  if (ratio <= 0.2) score = 95 + Math.round((1 - ratio / 0.2) * 5);     // 95..100
  else if (ratio <= 0.5) score = 80 + Math.round((1 - (ratio - 0.2) / 0.3) * 15); // 80..95
  else if (ratio <= 0.8) score = 60 + Math.round((1 - (ratio - 0.5) / 0.3) * 20); // 60..80
  else if (ratio <= 1.0) score = 40 + Math.round((1 - (ratio - 0.8) / 0.2) * 20); // 40..60
  else score = Math.max(20, 40 - Math.round((ratio - 1) * 30));         // out-of-tol soft

  score = Math.max(0, Math.min(100, score));

  // Detour delta: deviazione media oltre il tragitto "ideale".
  const detourDelta = +(((pickupDev + deliveryDev) / 2)).toFixed(1);

  let qualitative;
  if (score >= 90) qualitative = "Molto compatibile con il tuo tragitto";
  else if (score >= 75) qualitative = "Compatibile con il tuo tragitto";
  else if (score >= 55) qualitative = "Compatibile (vicino al limite)";
  else qualitative = "Marginalmente compatibile";

  return {
    score,                // 0..100
    pickupDeviationKm: +pickupDev.toFixed(1),
    deliveryDeviationKm: +deliveryDev.toFixed(1),
    detourDeltaKm: detourDelta,
    qualitative,
  };
}

/**
 * Optional: from `usual_time_window` "HH:MM-HH:MM" guess if commute is morning/evening.
 * Returns string or null.
 */
export function commuteTimingLabel(usualTimeWindow) {
  if (!usualTimeWindow || typeof usualTimeWindow !== "string") return null;
  const m = usualTimeWindow.match(/^(\d{1,2}):?(\d{0,2})\s*[-–]\s*(\d{1,2}):?/);
  if (!m) return null;
  const startHr = parseInt(m[1], 10);
  if (!Number.isFinite(startHr)) return null;
  if (startHr >= 5 && startHr < 11) return "Compatibile con il tragitto mattutino";
  if (startHr >= 11 && startHr < 15) return "Compatibile con il tragitto di metà giornata";
  if (startHr >= 15 && startHr < 22) return "Compatibile con il tuo rientro";
  return null;
}

// eslint-disable-next-line no-unused-vars
const _kmDegLat = KM_PER_DEG_LAT;
